import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle, Eye, Puzzle, UserPlus, UsersRound, Layers, Wrench, Package, MapPin, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Panel, StatCard, formatDateTime } from "@/components/admin/ui";
import { getSolutionIcon } from "@/lib/solutionIcons";
import { OrgOverviewHeader } from "@/components/org/overview/OrgOverviewHeader";
import { LocationPreview } from "@/components/org/settings/LocationPreview";
import { usePolling } from "@/hooks/usePolling";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrgOverview() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [solutionsData, setSolutionsData] = useState(null);
  const [team, setTeam] = useState(null);
  const [services, setServices] = useState(null);
  const [products, setProducts] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const org = user?.organization;
  const isOwner = user?.role === "org_owner";
  const workspaceReady = org?.status === "Active";
  const firstName = (user?.name || "there").split(" ")[0];

  const loadAll = useCallback((silent) => {
    const requests = [
      axios.get(`${API}/org/solutions`, { withCredentials: true }).then(({ data }) => setSolutionsData(data)).catch(() => { if (!silent) setSolutionsData({ solutions: [], all_solutions: [] }); }),
      axios.get(`${API}/org/services`, { withCredentials: true }).then(({ data }) => setServices(data.services || [])).catch(() => { if (!silent) setServices([]); }),
      axios.get(`${API}/org/products`, { withCredentials: true }).then(({ data }) => setProducts(data.products || [])).catch(() => { if (!silent) setProducts([]); }),
    ];
    if (isOwner) {
      requests.push(axios.get(`${API}/org/team`, { withCredentials: true }).then(({ data }) => setTeam(data)).catch(() => { if (!silent) setTeam(null); }));
    }
    Promise.all(requests).then(() => setLastUpdated(Date.now()));
  }, [API, isOwner]);

  useEffect(() => { loadAll(); }, [loadAll]);

  usePolling(() => loadAll(true), 10000);

  if (!workspaceReady) {
    return (
      <div className="mx-auto max-w-2xl py-10 text-center" data-testid="org-overview-pending">
        <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Hi {firstName}, welcome to {org?.name || "Nova Opsis"}.</h1>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">Nova Opsis is still finalizing your organization's setup. You'll get full access to your workspace as soon as it's activated.</p>
      </div>
    );
  }

  const solutions = solutionsData?.solutions || [];
  const allSolutions = solutionsData?.all_solutions || [];
  const addressLine = [
    org?.address?.street,
    org?.address?.city,
    [org?.address?.state, org?.address?.zip].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");
  const locationSubtitle = addressLine ? (
    <span className="inline-flex items-center gap-1.5">
      <MapPin size={13} className="shrink-0" />
      {addressLine}
    </span>
  ) : "Add your business address in Organization Settings.";
  const setupItems = isOwner ? [
    { key: "invite-team", label: "Invite your team", complete: !!team && team.members.length > 1 },
    { key: "add-service", label: "Add a service", complete: !!services && services.length > 0 },
  ] : [];
  const setupComplete = setupItems.filter((i) => i.complete).length;
  const setupPercent = setupItems.length ? Math.round((setupComplete / setupItems.length) * 100) : 0;

  const activity = [
    ...((team?.pending_invitations || []).map((inv) => ({ key: `inv-${inv.invite_id}`, title: "Team member invited", description: inv.email, timestamp: inv.created_at }))),
    ...((services || []).map((s) => ({ key: `svc-${s.service_id}`, title: "Service added", description: s.name, timestamp: s.created_at }))),
    ...((products || []).map((p) => ({ key: `prod-${p.product_id}`, title: "Product added", description: p.name, timestamp: p.created_at }))),
  ].filter((a) => a.timestamp).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 5);

  return (
    <div className="space-y-8" data-testid="org-overview-page">
      <div className="grid gap-4 lg:grid-cols-[2.3fr_1fr]">
        <div className="flex flex-col gap-4">
          <OrgOverviewHeader org={org} firstName={firstName} isOwner={isOwner} lastUpdated={lastUpdated} />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="org-overview-kpis">
            <StatCard icon={UsersRound} label="Team Members" value={team ? team.members.length : "—"} tone="blue" testId="org-kpi-team-members" />
            <StatCard icon={Layers} label="Active Solutions" value={solutions.length} tone="purple" testId="org-kpi-active-solutions" />
            <StatCard icon={Wrench} label="Services" value={services === null ? "—" : services.length} tone="cyan" testId="org-kpi-services" />
            <StatCard icon={Package} label="Products" value={products === null ? "—" : products.length} tone="green" testId="org-kpi-products" />
          </div>
        </div>
        <LocationPreview
          address={org?.address || {}}
          title="Location"
          subtitle={locationSubtitle}
          emptyMessage="Add your business address in Organization Settings to see a location preview."
          fill
          action={isOwner && (
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings")}
              data-testid="org-overview-view-edit-location"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors duration-200 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/5"
            >
              View / Edit Location <ExternalLink size={12} />
            </button>
          )}
          testId="org-overview-location-preview"
        />
      </div>

      <section>
        <h2 className="mb-3 text-lg font-bold text-slate-950 dark:text-white">Your Nova Solutions</h2>
        {isOwner && allSolutions.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-overview-all-solutions-grid">
            {allSolutions.map((s) => {
              const Icon = getSolutionIcon(s.icon);
              return s.enabled ? (
                <button key={s.solution_id} type="button" onClick={() => navigate(`/dashboard/solutions/${s.slug}`)} data-testid={`org-overview-solution-${s.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-[#111C33] dark:hover:border-blue-500/30">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#0052FF] dark:bg-blue-500/10 dark:text-blue-400">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <p className="mt-3 text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{s.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#0052FF] dark:text-blue-400">Open {s.name} <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" /></span>
                </button>
              ) : (
                <div key={s.solution_id} className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 opacity-60 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`org-overview-solution-disabled-${s.slug}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-500">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <p className="mt-3 text-sm font-bold text-slate-500 dark:text-slate-400">{s.name}</p>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Not available for your organization.</p>
                </div>
              );
            })}
          </div>
        ) : solutions.length === 0 ? (
          <EmptyState icon={Puzzle} title="No solutions enabled yet." message="Nova Opsis will enable your Solutions shortly." testId="org-overview-solutions-empty" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-overview-solutions-grid">
            {solutions.map((s) => {
              const Icon = getSolutionIcon(s.icon);
              return (
                <button key={s.solution_id} type="button" onClick={() => navigate(`/dashboard/solutions/${s.slug}`)} data-testid={`org-overview-solution-${s.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-[#111C33] dark:hover:border-blue-500/30">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#0052FF] dark:bg-blue-500/10 dark:text-blue-400">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <p className="mt-3 text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{s.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#0052FF] dark:text-blue-400">Open {s.name} <ArrowRight size={12} /></span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {isOwner && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="Setup Progress" testId="org-overview-setup-progress">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-slate-950 dark:text-white">{setupPercent}%</span>
              <span className="text-sm text-slate-500 dark:text-slate-400">complete</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
              <div className="h-full rounded-full bg-gradient-to-r from-[#0052FF] to-[#00D4FF] transition-all duration-500" style={{ width: `${setupPercent}%` }} />
            </div>
            <div className="mt-4 space-y-2">
              {setupItems.map((item) => (
                <div key={item.key} className="flex items-center gap-2 text-sm">
                  {item.complete ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Circle size={16} className="text-slate-300 dark:text-slate-600" />}
                  <span className={item.complete ? "text-slate-700 dark:text-slate-300" : "text-slate-500 dark:text-slate-500"}>{item.label}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recent Activity" testId="org-overview-recent-activity">
            {activity.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="org-overview-activity-empty">No recent activity yet.</p>
            ) : (
              <div className="space-y-3">
                {activity.map((a) => (
                  <div key={a.key} className="flex items-center justify-between gap-3 text-sm">
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{a.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{a.description}</p>
                    </div>
                    <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{formatDateTime(a.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {isOwner && team && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">Team Access at a Glance</h2>
            <button type="button" onClick={() => navigate("/dashboard/team")} data-testid="org-overview-manage-team" className="text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">Manage Team & Access →</button>
          </div>
          {team.members.length === 0 ? (
            <EmptyState icon={UserPlus} title="No team members yet." message="Invite your first Organization User from Team & Access." testId="org-overview-team-empty" />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#111C33]">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.1em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pl-5 pr-4 font-bold">User</th>
                    {team.solutions.map((s) => <th key={s.solution_id} className="py-3 pr-4 font-bold">{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {team.members.map((m) => (
                    <tr key={m.user_id} className="border-b border-slate-100 last:border-0 dark:border-white/5">
                      <td className="py-3 pl-5 pr-4 font-semibold text-slate-900 dark:text-white">{m.name} {m.is_owner && <span className="text-slate-400 dark:text-slate-500">(Owner)</span>}</td>
                      {team.solutions.map((s) => {
                        const level = m.is_owner ? "manage" : (m.solution_access?.[s.solution_id] || "none");
                        return (
                          <td key={s.solution_id} className="py-3 pr-4">
                            {level === "manage" ? <CheckCircle2 size={16} className="text-emerald-500" /> : level === "view" ? <Eye size={16} className="text-blue-400" /> : <span className="text-slate-300 dark:text-slate-600">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">Your Services</h2>
          <button type="button" onClick={() => navigate("/dashboard/services")} data-testid="org-overview-manage-services" className="text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">Manage Services →</button>
        </div>
        {services === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading services…</div>
        ) : services.length === 0 ? (
          <EmptyState icon={Wrench} title="No services yet." message="Add your first service from the Services page." testId="org-overview-services-empty" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-overview-services-list">
            {services.slice(0, 4).map((s) => {
              const primary = (s.images || []).find((img) => img.is_primary) || (s.images || [])[0];
              return (
                <div key={s.service_id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#111C33]">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 dark:border-white/15">
                    {primary ? <img src={`${BACKEND_URL}${primary.url}`} alt={s.name} className="h-full w-full object-cover" /> : <Wrench size={16} className="text-slate-300 dark:text-slate-600" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{s.active ? "Active" : "Inactive"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">Your Products</h2>
          <button type="button" onClick={() => navigate("/dashboard/products")} data-testid="org-overview-manage-products" className="text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">Manage Products →</button>
        </div>
        {products === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading products…</div>
        ) : products.length === 0 ? (
          <EmptyState icon={Package} title="No products yet." message="Add your first product from the Products page." testId="org-overview-products-empty" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-overview-products-list">
            {products.slice(0, 4).map((p) => {
              const primary = (p.images || []).find((img) => img.is_primary) || (p.images || [])[0];
              return (
                <div key={p.product_id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#111C33]">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 dark:border-white/15">
                    {primary ? <img src={`${BACKEND_URL}${primary.url}`} alt={p.name} className="h-full w-full object-cover" /> : <Package size={16} className="text-slate-300 dark:text-slate-600" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">{p.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{p.active ? "Active" : "Inactive"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
